'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import { ReactNode, useState } from 'react';

import { useCurrency } from '@/hooks/useCurrency';

import { historicalRateCauses } from '../data/historicalRateCauses';
import { projectionSources } from '../data/rateProjections';
import { BillPoint } from '../engine';

import { signedPercent } from './chartFormat';

type Props = {
  /** 2005–2020 back-cast so the ledger reaches as far back as the chart */
  deepHistory?: BillPoint[];
  points: BillPoint[];
  stateCode: string;
};

type BadgeVariant = 'announced' | 'neutral' | 'planned' | 'unplanned';

// Reuses the marketplace badge tokens (see DesignSystemProvider) — blue for
// regulator-announced, yellow for market-driven, plain for neutral rows
const BADGE_TOKENS: Record<BadgeVariant, { bgColor: string; color: string }> = {
  announced: { bgColor: 'uiBadge.bestDeal.bgColor', color: 'uiBadge.bestDeal.textColor' },
  neutral: { bgColor: 'uiBadge.default.bgColor', color: 'uiBadge.default.textColor' },
  planned: { bgColor: 'uiBadge.bestDeal.bgColor', color: 'uiBadge.bestDeal.textColor' },
  unplanned: { bgColor: 'uiBadge.fairDeal.bgColor', color: 'uiBadge.fairDeal.textColor' },
};

const Badge = ({ label, variant }: { label: string; variant: BadgeVariant }) => (
  <DS.Flex
    bg={BADGE_TOKENS[variant].bgColor}
    border="1px solid"
    borderColor={BADGE_TOKENS[variant].color}
    borderRadius={4}
    color={BADGE_TOKENS[variant].color}
    px={1}
    w="fit-content"
  >
    <DS.Text text="body9">{label}</DS.Text>
  </DS.Flex>
);

type Row = {
  badgeLabel: string;
  badgeVariant: BadgeVariant;
  cause: string;
  sourceUrl?: string;
  year: number;
  /** Set when consecutive identical rows collapse into one multi-year line */
  yearEnd?: number;
  yoyFraction: number;
};

const forecastCells = (point: BillPoint) => {
  if (point.forecast === 'STEO') {
    return {
      badgeLabel: 'Forecast',
      badgeVariant: 'neutral' as const,
      cause: `${projectionSources.steo.label}: national forecast, no state filing on record`,
      sourceUrl: projectionSources.steo.url,
    };
  }
  if (point.forecast === 'RECENT') {
    return {
      badgeLabel: 'Recent pace',
      badgeVariant: 'neutral' as const,
      cause: 'Your state’s last decade continues at its actual 2015–2025 pace (EIA)',
      sourceUrl: projectionSources.history.url,
    };
  }
  if (point.forecast === 'BLEND') {
    return {
      badgeLabel: 'Blended',
      badgeVariant: 'neutral' as const,
      cause: 'Average of the EIA outlook and your state’s recent pace',
      sourceUrl: projectionSources.aeo.url,
    };
  }
  return {
    badgeLabel: 'Outlook',
    badgeVariant: 'neutral' as const,
    cause: `${projectionSources.aeo.label} — long-term path for your region`,
    sourceUrl: projectionSources.aeo.url,
  };
};

const projectedRow = (point: BillPoint): Row => ({
  ...(point.knownChange
    ? {
        badgeLabel: 'Announced',
        badgeVariant: 'announced' as const,
        cause: point.knownChange.note,
        sourceUrl: point.knownChange.sourceUrl,
      }
    : forecastCells(point)),
  year: point.year,
  yoyFraction: point.yoyFraction ?? 0,
});

const actualRow = (point: BillPoint, stateCode: string): Row => {
  const known = historicalRateCauses[stateCode]?.[point.year];
  return {
    ...(known
      ? {
          badgeLabel: known.planned ? 'Planned' : 'Unplanned',
          badgeVariant: known.planned ? ('planned' as const) : ('unplanned' as const),
          cause: known.cause,
          sourceUrl: known.sourceUrl,
        }
      : {
          badgeLabel: 'Actual',
          badgeVariant: 'neutral' as const,
          cause: 'Statewide average residential rate change (EIA)',
        }),
    year: point.year,
    yoyFraction: point.yoyFraction ?? 0,
  };
};

// A 19-year run of identical scenario rows reads as one multi-year line —
// but only when the percentage is identical too, or the range would lie
const groupRuns = (rows: Row[]): Row[] =>
  rows.reduce<Row[]>((grouped, row) => {
    const previous = grouped[grouped.length - 1];
    if (
      previous &&
      previous.badgeLabel === row.badgeLabel &&
      previous.cause === row.cause &&
      Math.abs(previous.yoyFraction - row.yoyFraction) < 1e-9
    ) {
      grouped[grouped.length - 1] = { ...previous, yearEnd: row.year };
      return grouped;
    }
    return [...grouped, row];
  }, []);

const LedgerRows = ({ rows }: { rows: Row[] }) => {
  const { formatPercent } = useCurrency();
  return (
    <DS.Flex as="ul" gap={0} listStyleType="none" m={0} p={0}>
      {rows.map(({ badgeLabel, badgeVariant, cause, sourceUrl, year, yearEnd, yoyFraction }) => (
        <DS.Flex
          alignItems="baseline"
          as="li"
          borderColor="uiDivider"
          borderTop="1px solid"
          flexDirection="row"
          flexWrap="wrap"
          gap={2}
          key={year}
          py={2}
        >
          <DS.Text fontWeight="medium" text="body14" w={yearEnd ? '96px' : '44px'}>
            {yearEnd ? `${year}–${yearEnd}` : year}
          </DS.Text>
          <DS.Text text="body14" w="66px">
            {signedPercent(yoyFraction, formatPercent)}
            {yearEnd ? '/yr' : ''}
          </DS.Text>
          <Badge label={badgeLabel} variant={badgeVariant} />
          <DS.Text color="uiHelperCopy" flex="1" minW="220px" text="body14">
            <span>{cause}</span>
            {sourceUrl && (
              <span>
                {' '}
                <a href={sourceUrl} rel="noreferrer" target="_blank">
                  Source
                </a>
              </span>
            )}
          </DS.Text>
        </DS.Flex>
      ))}
    </DS.Flex>
  );
};

export const RateChangeLedger = ({ deepHistory, points, stateCode }: Props) => {
  const [expanded, setExpanded] = useState(false);

  const history = points.filter(({ isProjected }) => !isProjected);
  const today = history[history.length - 1];
  const projected = points.filter(({ isProjected }) => isProjected);

  // One continuous 2005→latest-EIA-year sequence; YoY recomputed from the
  // bills so the seam year between the two EIA series gets its real delta.
  // Today's step already leads the forward section, so it stays out of history.
  const merged = [...(deepHistory ?? []), ...history.filter(({ year }) => year !== today.year)];
  const actualPoints = merged
    .map((point, i) =>
      i === 0 ? null : { ...point, yoyFraction: point.monthlyBillUsd / merged[i - 1].monthlyBillUsd - 1 }
    )
    .filter((point): point is BillPoint & { yoyFraction: number } => point !== null);

  // Soonest first, starting with this year's step; long identical runs of
  // scenario years collapse into one multi-year line
  const forwardRows = groupRuns([today, ...projected].map(projectedRow));
  const actualRows = [...actualPoints].reverse().map((point) => actualRow(point, stateCode));

  return (
    // Reading matter breaks from the page's centered rhythm: left-set heading
    // over left-set rows, with the expander hanging off the rows' own axis
    <DS.Flex aria-label="Why your bill increases" as="section" gap={3} maxW="720px" mx="auto" textAlign="left" w="100%">
      <x.h2 text={{ _: 'h6', md: 'h5' }}>Why your bill increases</x.h2>
      <DS.Text color="uiHelperCopy" text="body14">
        Announced changes come from utility rate cases on file with your state’s regulator.
      </DS.Text>

      <LedgerRows rows={forwardRows} />

      {expanded && (
        <>
          <Overline>The last {actualRows.length} years</Overline>
          <LedgerRows rows={actualRows} />
        </>
      )}

      {/* uiLink + chevron reads as a control; the black heading reads as a heading */}
      <DS.Button
        alignSelf="flex-start"
        aria-expanded={expanded}
        color="uiLink"
        icon={expanded ? DS.IconMiniChevronUp : DS.IconMiniChevronDown}
        iconPosition="right"
        onClick={() => setExpanded((current) => !current)}
        size="sm"
        variant="text"
      >
        {expanded ? 'Show less' : `Show ${actualRows.length} years of rate history`}
      </DS.Button>
    </DS.Flex>
  );
};

const Overline = ({ children }: { children: ReactNode }) => (
  <x.h3 color="uiHelperCopy" letterSpacing="0.06em" text="body12" textTransform="uppercase">
    {children}
  </x.h3>
);
